import type { EmployeeCategory, AdminEmployee, MonthlyRecord } from '../types/models';

// 給与計算の上書き値
export interface PayrollOverride {
  id: string;
  employeeId: string;
  periodKey: string;
  fieldName: string;
  overrideValue: number;
}

// 正職員の給与計算結果
export interface FulltimePayroll {
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  attendanceDays: number;        // 出勤日数 (夜勤=2日換算、休日出勤は含まない)
  attendanceCount: number;       // 出勤回数 (実際の出勤回数、休日出勤は含まない)
  holidayWorkDays: number;       // 休日出勤日数 (別計上)
  holidayWorkMinutes: number;    // 休日出勤時間 (別計上、minutes)
  paidLeaveDays: number;         // 有給日数
  absenceDays: number;           // 欠勤日数 (manual)
  lateEarlyMinutes: number;      // 遅刻早退時間 (manual, in minutes)
  totalWorkMinutes: number;      // 勤務時間 (休日出勤は含まない、minutes)
  overtimeMinutes: number;       // 普残時間
  nightShiftUpTo4: number;       // 夜勤~4回
  nightShiftOver4: number;       // 5回目~
  nightShiftTotal: number;       // 夜勤合計
  specialBenefit: number;        // 特別給付金 (固定額 from master)
  specialAllowance: number;      // 特別手当 (固定額 from master)
  transportAllowance: number;    // 交通費 (休日出勤も対象)
  mealDeduction: number;         // 控除食事代 (休日出勤は対象外)
}

// 非常勤（パート＋旧夜勤）の給与計算結果
export interface ParttimePayroll {
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  attendanceDays: number;        // 出勤日数 (休日出勤は含まない)
  holidayWorkDays: number;       // 休日出勤日数 (別計上)
  holidayWorkMinutes: number;    // 休日出勤時間 (別計上、minutes)
  paidLeaveDays: number;         // 有給日数
  absenceDays: number;           // 欠勤日数 (manual)
  lateEarlyMinutes: number;      // 遅刻早退時間 (manual, in minutes)
  totalWorkMinutes: number;      // 勤務時間 (休日出勤は含まない、minutes)
  overtimeMinutes: number;       // 残業時間
  attendanceTotal: number;       // 出勤数 (通常出勤のみ、休日出勤は含まない)
  specialBenefit: number;        // 特別給付金 (hours * unit_price)
  specialAllowance: number;      // 特別手当 (hours * unit_price)
  transportAllowance: number;    // 交通費 (休日出勤も対象)
  mealDeduction: number;         // 控除食事代 (休日出勤は対象外)
}

// PayrollTab に渡すprops
export interface PayrollTabProps {
  employees: AdminEmployee[];
  monthlyData: Record<string, MonthlyRecord[]>;
  selectedMonth: string;
  closingDay: string;
  calculateOvertime: (record: MonthlyRecord) => number;
  supabaseClient: any;
  monthlyLoading: boolean;
  onMonthChange: (month: string) => void;
  payrollSourceFingerprint?: string;
  payrollSourceError?: string;
  onReloadMonthly?: () => void;
}

// 上書き可能フィールドの定義
export type OverridableField = {
  fieldName: string;
  label: string;
  category: EmployeeCategory;
  type: 'number' | 'minutes';
};

export const FULLTIME_OVERRIDABLE_FIELDS: OverridableField[] = [
  { fieldName: 'absence_days', label: '欠勤日数', category: 'fulltime', type: 'number' },
  { fieldName: 'late_early_minutes', label: '遅刻早退', category: 'fulltime', type: 'minutes' },
  { fieldName: 'special_benefit', label: '特別給付金', category: 'fulltime', type: 'number' },
  { fieldName: 'special_allowance', label: '特別手当', category: 'fulltime', type: 'number' },
  { fieldName: 'transport_allowance', label: '交通費', category: 'fulltime', type: 'number' },
  { fieldName: 'meal_deduction', label: '控除食事代', category: 'fulltime', type: 'number' },
];

export const PARTTIME_OVERRIDABLE_FIELDS: OverridableField[] = [
  { fieldName: 'absence_days', label: '欠勤日数', category: 'parttime', type: 'number' },
  { fieldName: 'late_early_minutes', label: '遅刻早退', category: 'parttime', type: 'minutes' },
  { fieldName: 'special_benefit', label: '特別給付金', category: 'parttime', type: 'number' },
  { fieldName: 'special_allowance', label: '特別手当', category: 'parttime', type: 'number' },
  { fieldName: 'transport_allowance', label: '交通費', category: 'parttime', type: 'number' },
  { fieldName: 'meal_deduction', label: '控除食事代', category: 'parttime', type: 'number' },
];

// 時間表示ヘルパー
export const minutesToHoursMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

export const minutesToDecimalHours = (minutes: number): string => {
  return (minutes / 60).toFixed(1);
};
